import { parseCsv } from '@/lib/csv';

/**
 * The item export, saved and read back: `GET /v1/reports/items.csv`.
 *
 * ## The download itself is `cash-book-export.ts`, deliberately
 *
 * FE050 built the mechanism and this module uses it unchanged. `ApiClient.download`
 * fetches the bytes with the credential attached and `saveDownloadedFile` there
 * turns bytes already in hand into a saved file. Both notes are worth reading
 * before touching either — in short, an anchor pointed at `/v1/reports/items.csv`
 * **cannot work**, because the route reads its principal from `Authorization:
 * Bearer` and from nowhere else, so a plain navigation saves a `401` envelope
 * with `.csv` on the end and nothing about it looks wrong until somebody opens
 * it.
 *
 * That argument is at its sharpest here. This export is `@RequireRole('OWNER')`
 * and carries `costPaise`, `refundedCostPaise`, `voidedCostPaise` and
 * `marginPaise` **per item** — the park's whole buying position in one file,
 * where the profit and loss yields an item's cost only by subtraction over a
 * window the caller narrows. A file that is quietly a refusal envelope is bad;
 * this one being quietly a refusal envelope, filed and read back a quarter
 * later, is the worst of the three.
 *
 * ## Every fetch of this file is a recorded act, so nothing here fetches
 *
 * `ExportService.openParkItems` writes an append-only audit entry **per export**
 * — who exported what, over which period, and how much left the system as
 * `itemCount`, `dayCount` and `breakdownRowCount`. It is the one "read" in this
 * epic that writes, and the JSON read it renders writes nothing.
 *
 * So this module holds no fetching of its own and `ItemExport.tsx` fetches only
 * from a click handler: no pre-fetch, no speculative fetch on hover or focus or
 * mount, and **no automatic retry** — a failed export is offered again as a
 * button, because a retry loop would be a person recorded as having disclosed
 * the park's buying position five times when they asked once.
 *
 * ## Why this module reads the file back, when its two siblings do not
 *
 * The cash book's and the profit and loss's exports save bytes and never look
 * inside them. This one is asked for something they were not: a saved file's
 * totals must reconcile against the figures on screen for the same range. The
 * file states its own totals on its last line, so the honest way to show that
 * they reconcile is to read the line — rather than to assert it in a comment and
 * hope.
 *
 * ## The mapping is the file's own header row, and there is no copy of it here
 *
 * There is no checked-in list of the export's twenty-seven columns, for the same
 * reason there is no longer a rebuilt filename in `cash-book-export.ts`: a copy
 * of a rule that lives in another repository goes stale silently. {@link
 * readItemsCsv} builds its mapping from the header the file itself declares and
 * reads every row through it — which is what makes "one column mapping parses
 * both the plain export and the `byDay` one" a property of the parser rather
 * than a claim about two hard-coded lists.
 *
 * Only two column names appear in this file, {@link RECORD_TYPE_COLUMN} and
 * {@link ITEM_COUNT_COLUMN}, and both are named in the ticket's own description
 * of the shape: the discriminator, and the one figure the completeness marker
 * carries.
 *
 * ## The reader itself now lives in `lib/csv.ts`
 *
 * It was private to this file until FE208, which reads a catalogue file a person
 * hands the app. Two CSV readers in one app are two opinions about what a quoted
 * comma means, and `docs/ui-conventions.md` asks for one — so it moved out
 * unchanged rather than being written a second time. Nothing about the behaviour
 * relied on here has moved with it.
 *
 * ## What it does not do to the values it reads
 *
 * Nothing. A cell comes back as the text the file carried, money included —
 * whole paise, no symbol, no grouping, no division. `lib/money.ts` is the one
 * place paise become rupees and it runs at the render edge; arithmetic on a
 * formatted value is what Constitution Principle VI exists to prevent.
 *
 * `csv-writer.ts` in the API prefixes a **string** whose first character is `=`,
 * `+`, `-`, `@`, TAB or CR with a single quote, so a spreadsheet reads it as text
 * rather than as a formula. That prefix is **left in place**: it is a character
 * the server put in the file, stripping it would be this module guessing which
 * ones were the server's and which were the data's, and no figure this app reads
 * back can carry one — numbers are never prefixed, by that writer's own rule.
 */

/** What the item export is saved as when the server's own name did not arrive. */
export const ITEMS_DEFAULT_FILENAME = 'items.csv';

/**
 * The column the four row kinds are discriminated by.
 *
 * `DAY`, `ITEM`, `ITEM_DAY` and `TOTAL` in one file rather than in four, which
 * is the shape the ticket describes and the API commits to.
 */
export const RECORD_TYPE_COLUMN = 'recordType';

/** The one figure the completeness marker carries. Blank on every other row. */
export const ITEM_COUNT_COLUMN = 'itemCount';

/** The completeness marker's own `recordType`. The body must end on one. */
const TOTAL_RECORD_TYPE = 'TOTAL';

/** One row, its cells keyed by the header the file declared. */
export interface ItemsCsvRow {
  /** `DAY`, `ITEM`, `ITEM_DAY` or `TOTAL` — read, not narrowed: see {@link readItemsCsv}. */
  readonly recordType: string;

  /** Every column of the header, in its order, against this row's text. */
  readonly cells: ReadonlyMap<string, string>;
}

/** A parsed export: the mapping it declared, and the rows read through it. */
export interface ItemsCsvFile {
  /** The header row, in file order. The mapping, and the only one. */
  readonly columns: readonly string[];

  readonly rows: readonly ItemsCsvRow[];
}

/**
 * What a complete file says about itself: the figure on its marker, and how many
 * rows of each kind it carried.
 *
 * These are the three sizes `ExportService.openParkItems` records in its audit
 * entry — `itemCount`, `dayCount` and `breakdownRowCount` — which is why they
 * are the three worth showing back: they are what the disclosure was recorded
 * as, counted from the file that actually arrived.
 */
export interface ItemsCsvTotals {
  /** The marker's `itemCount`: the length of the list the JSON read publishes. */
  readonly itemCount: number;

  readonly dayRows: number;
  readonly itemRows: number;

  /** The `ITEM_DAY` rows `?byDay=true` added. Zero on the plain export. */
  readonly breakdownRows: number;
}

/**
 * The bytes of an item export, read.
 *
 * `null` where they are not one: an empty body, or a header with no
 * {@link RECORD_TYPE_COLUMN} in it. That is the narrowest test that tells this
 * file from the two beside it and from a refusal envelope saved with a `.csv` on
 * the end, and it is deliberately not a check against a list of twenty-seven
 * names — a column added upstream must widen this reader's mapping, not break it.
 *
 * `recordType` is carried as the `string` the file held and **not** narrowed to
 * a union. A fifth kind arriving is a file this app should still read and count
 * rather than refuse, and a cast would be this module asserting something about
 * bytes it did not write.
 *
 * A row with fewer cells than the header has is read with the missing ones
 * empty, and any beyond the header's width are dropped. Neither is expected —
 * the API writes every row from one column table — but a parser that threw on it
 * would turn a slightly odd file into no answer at all, and the marker check in
 * {@link totalsOfItemsCsv} is the real guard on completeness.
 */
export function readItemsCsv(text: string): ItemsCsvFile | null {
  const [header, ...body] = parseCsv(text);

  if (header === undefined || !header.includes(RECORD_TYPE_COLUMN)) return null;

  return {
    columns: header,
    rows: body.map((cells) => {
      const mapped = new Map(header.map((column, at) => [column, cells[at] ?? '']));

      return { recordType: mapped.get(RECORD_TYPE_COLUMN) ?? '', cells: mapped };
    }),
  };
}

/**
 * What a file says it holds, or `null` where it does not end on its marker.
 *
 * **"A body that does not end on a `TOTAL` row is incomplete"** is the contract
 * the export controller states for all three exports and the only thing a reader
 * can check, so a truncated stream answers `null` rather than a count of the
 * rows that happened to arrive. A total counted from what was written agrees
 * with itself after a truncation, which is precisely the failure the marker
 * exists to make visible.
 *
 * The row counts beside it *are* counted from what arrived, which is the point:
 * they are only ever reported alongside a marker that says the file is whole.
 */
export function totalsOfItemsCsv(file: ItemsCsvFile): ItemsCsvTotals | null {
  const last = file.rows.at(-1);

  if (last === undefined || last.recordType !== TOTAL_RECORD_TYPE) return null;

  const itemCount = wholeCount(last.cells.get(ITEM_COUNT_COLUMN) ?? '');

  if (itemCount === null) return null;

  const of = (kind: string) => file.rows.filter((row) => row.recordType === kind).length;

  return {
    itemCount,
    dayRows: of('DAY'),
    itemRows: of('ITEM'),
    breakdownRows: of('ITEM_DAY'),
  };
}

/**
 * A cell as a count, or `null` for anything that is not one.
 *
 * `Number('')` is `0` and `Number(' ')` is `0`, so an empty marker would read as
 * a period in which nothing traded — which is a real and different answer. The
 * digits are matched before the conversion rather than after it.
 */
function wholeCount(cell: string): number | null {
  return /^\d+$/.test(cell.trim()) ? Number(cell.trim()) : null;
}
